<template>
  <div class="new-live-metrics" style="overflow: hidden">
    <div class="d-flex">
      <h2 class="page-label ml-3">
        {{ $t('message.pages.live.metrics.viewers') }}
      </h2>
      <v-tooltip right>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon x-small class="ml-1 pb-1" v-bind="attrs" v-on="on">
            <v-icon style="color: var(--v-neutral-lighten1)" size="18">
              mdi-help-circle
            </v-icon>
          </v-btn>
        </template>
        <v-container
          class="text-body-2 pa-0"
          style="max-width: 250px"
          v-html="$t('message.pages.live.viewersDescription')"
        ></v-container>
      </v-tooltip>
    </div>
    <v-skeleton-loader
      v-if="isLoading"
      width="100%"
      height="200"
      type="image"
    />
    <apexchart
      v-if="!isLoading"
      :class="{ darkThemeSvgFix: $vuetify.theme.dark }"
      ref="chart"
      type="area"
      height="200"
      :options="chartOptions"
      :series="series"
    ></apexchart>

    <div v-if="!isLoading" class="d-flex mt-4">
      <div class="d-flex flex-column mr-14 ml-5">
        <span class="values" style="color: var(--v-neutral-base)">{{
          $t('message.pages.live.metrics.currentViewers')
        }}</span>
        <span
          v-if="liveStatus === 'offline' || liveStatus === 'finished'"
          class="actual-value"
        >
          -
        </span>
        <span v-else class="actual-value">{{ actualFrameRate }} </span>
      </div>

      <div class="d-flex flex-column">
        <span class="values" style="color: var(--v-neutral-base)">{{
          $t('message.pages.live.metrics.averageViewers')
        }}</span>
        <span v-if="liveStatus === 'offline'" class="average-value"> - </span>
        <span v-else class="average-value">{{ averageViewers }} </span>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import liveService from '@/services/liveServiceV2'

export default {
  name: 'NewLiveV2Metrics',
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      liveTime: [],
      framerateData: [],
      currentDate: new Date(),
      isLoading: true,
      timestampStarted: 0,
      isComponentAlive: true,
      averageViewers: 0,
      actualFrameRate: 0,
      liveStatus: 'offline',
      chartOptions: {
        chart: {
          id: 'area-chart',
          type: 'area',
          toolbar: {
            show: false,
          },
        },
        colors: ['#3F55EE'],
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.9,
            stops: [0, 90, 100],
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth',
          width: 2,
        },
        xaxis: {
          categories: [],
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          labels: {
            show: false,
          },
        },
        yaxis: {
          min: 0,

          labels: {
            formatter: function (value) {
              return value
            },
          },
        },
        tooltip: {
          theme: 'dark',
        },
      },

      series: [
        {
          name: 'Viewers',
          data: [],
        },
      ],
    }
  },
  created() {
    this.isComponentAlive = true
    this.$root.$on('statusChanged', (status) => {
      this.liveStatus = status
    })
  },
  mounted() {
    this.$vuetify.theme.dark
      ? (this.chartOptions.tooltip.theme = 'dark')
      : (this.chartOptions.tooltip.theme = 'light')
    this.startMetricsUpdate()
    this.getMetrics()
  },
  beforeDestroy() {
    this.isComponentAlive = false
    this.stopMetricsUpdate()
    this.liveStatus = 'offline'
  },

  methods: {
    async updateMetrics() {
      if (!this.isComponentAlive) return
      if (!this.$route.params.id) return
      if (this.$root.liveStatus === 'online') {
        const metrics = await liveService.getViewers(this.$route.params.id)
        this.actualFrameRate = metrics.data.viewers
        if (isNaN(this.timestampStarted)) {
          let initTimestamp = Date.now() - this.currentDate.getTime()
          this.liveTime.push(this.totalTimeToUpdate(initTimestamp))
        } else {
          let timestamp = Date.now()
          this.liveTime.push(this.totalTime(timestamp, this.timestampStarted))
        }
        this.chartOptions.xaxis.categories = [...this.liveTime]
        if (
          this.$refs.chart &&
          typeof this.$refs.chart.updateOptions === 'function'
        ) {
          this.$refs.chart.updateOptions({
            xaxis: this.chartOptions.xaxis,
          })
        }
        this.addValue(this.actualFrameRate)
        let sum = this.series[0].data.reduce((accumulator, currentValue) => {
          return accumulator + currentValue
        }, 0)
        this.averageViewers = sum / this.series[0].data.length
        this.averageViewers = this.averageViewers.toFixed(0)
      }
    },

    startMetricsUpdate() {
      this.updateInterval = setInterval(this.updateMetrics, 4000)
    },
    stopMetricsUpdate() {
      clearInterval(this.updateInterval)
    },

    async addValue(value) {
      this.series[0].data.push(value)
      if (
        this.$refs.chart &&
        typeof this.$refs.chart.updateSeries === 'function'
      ) {
        this.$refs.chart.updateSeries(this.series)
      }
    },

    totalTime(timestamp, timestampstart) {
      const diff = timestamp - timestampstart
      const seconds = Math.floor(diff / 1000)
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secondsRestantes = seconds % 60
      const formattedHours = hours < 10 ? `0${hours}` : `${hours}`
      const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`
      const formattedSeconds =
        secondsRestantes < 10 ? `0${secondsRestantes}` : `${secondsRestantes}`
      return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
    },

    totalTimeToUpdate(time) {
      const seconds = Math.floor(time / 1000)
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secondsRestantes = seconds % 60
      const formattedHours = hours < 10 ? `0${hours}` : `${hours}`
      const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`
      const formattedSeconds =
        secondsRestantes < 10 ? `0${secondsRestantes}` : `${secondsRestantes}`
      return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
    },

    async getMetrics() {
      try {
        await this.getLive()
        const metrics = await liveService.getViewersHistoric(
          this.$route.params.id
        )

        if (
          metrics &&
          metrics.data &&
          Array.isArray(metrics.data) &&
          metrics.data.length > 0
        ) {
          metrics.data.forEach((metric) => {
            const metricParsed =
              typeof metric === 'string' ? JSON.parse(metric) : metric

            if (
              metricParsed &&
              metricParsed.viewers !== null &&
              metricParsed.viewers !== '' &&
              metricParsed.viewers !== 'null'
            ) {
              this.framerateData.push(metricParsed.viewers)
              this.liveTime.push(
                this.totalTime(metricParsed.timestamp, this.timestampStarted)
              )
            }
          })
        }

        this.liveTime.reverse()
        this.chartOptions.xaxis.categories = this.liveTime
        this.series[0].data = this.framerateData

        try {
          if (
            this.$refs.chart &&
            typeof this.$refs.chart.updateSeries === 'function'
          ) {
            this.$refs.chart.updateSeries(this.series)
            if (
              this.$refs.chart &&
              typeof this.$refs.chart.updateOptions === 'function'
            ) {
              this.$refs.chart.updateOptions({
                xaxis: this.chartOptions.xaxis,
              })
            }
          }
        } catch (error) {
          console.error(error)
        }

        const status = this.$root.liveStatus
        if (status !== 'online') {
          this.actualFrameRate = '-'
          let sum = this.series[0].data.reduce(
            (accumulator, currentValue) => accumulator + currentValue,
            0
          )
          this.averageViewers = (sum / this.series[0].data.length).toFixed(0)
        }

        this.startMetricsUpdate()
      } catch (error) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },

    async getLive() {
      const response = await liveService.getLive(this.$route.params.id)
      const dataString = response.data.started_at
      this.timestampStarted = Date.parse(dataString)
      this.liveStatus = response.data.status
    },
  },
}
</script>

<style lang="scss" scoped>
.actual-value {
  font-size: 20px;
  font-weight: 400;
  line-height: 24px;
  color: var(--v-brand-base);
}

.average-value {
  font-size: 20px;
  font-weight: 400;
  line-height: 24px;
  color: var(--v-neutral-lighten1);
}

.darkThemeSvgFix {
  svg text {
    fill: white;
  }

  .apexcharts-legend-text {
    color: white !important;
  }
}

.new-live-metrics {
  font-size: 0.875rem;
  color: var(--v-neutral-lighten1);
}

.bg-dark {
  .new-live-metrics {
    background-color: var(--v-neutral-darken2);
    color: white;
  }

  .page-label {
    color: var(--v-primary-base) !important;
  }

  .values {
    color: var(--v-primary-base) !important;
  }

  .page-title {
    color: var(--v-primary-base) !important;
  }
}
</style>
